import { Col, Row } from "antd";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Form from "../../form-components/from";
import { INITIAL_PAGE } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import {
  NotificationType,
  TopicType,
  getNotificationType,
} from "../../../models/general-notification/enum";
import GeneralNotificationContext from "../../../context/general-notification/context";
import EndPoints from "../../../services/end-points";

interface IProps {}

const GeneralNotificationFilter: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const { actions, query } = useContext(GeneralNotificationContext);
  const [selectedType, setSelectedType] = useState<number | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    const fetchItems = async () => {
      if (!selectedType) return;
      
      setItemsLoading(true);
      try {
        let response;
        switch (selectedType) {
          case NotificationType.CAMPAIGN:
            response = await EndPoints.campaign.getAllCampaigns({ light: true, perPage: -1 });
            setItems(response.data.data.map((item: any) => ({
              label: item.name,
              value: item.id
            })));
            break;
          case NotificationType.CHARITY:
            response = await EndPoints.charity.getAllCharities({ light: true, perPage: -1 });
            setItems(response.data.data.map((item: any) => ({
              label: item.name,
              value: item.id
            })));
            break;
          case NotificationType.ORPHAN:
            response = await EndPoints.orphan.getAllOrphans({ light: true, perPage: -1 });
            setItems(response.data.data.map((item: any) => ({
              label: `${item.firstName} ${item.lastName}`,
              value: item.id
            })));
            break;
        }
      } catch (error) {
        console.error('Error fetching items:', error);
      } finally {
        setItemsLoading(false);
      }
    };

    fetchItems();
  }, [selectedType]);

  return (
    <Form
      formId="general-notification-filter"
      onSubmit={(data) => {
        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword: data?.keyword,
          filters: [
            data?.topic
              ? {
                  name: "topic",
                  value: data.topic,
                  operation: "eq",
                }
              : undefined,
            data?.type
              ? {
                  name: "type",
                  value: data.type,
                  operation: "eq",
                }
              : undefined,
            data?.itemId && selectedType
              ? {
                  name: "itemId",
                  value: data.itemId,
                  operation: "eq",
                }
              : undefined,
          ].filter((f) => f),
        };
        actions.setQuery(filter);
      }}
    >
      <Row justify={"start"} gutter={[10, 0]}>
        <Col span={8}>
          <FieldBuilder
            name="topic"
            label={`${t("user_slide")}`}
            width="large"
            input={{
              type: "select",
              options: Object.entries(TopicType).map(([key, value]) => ({
                label: t(`${value}`),
                value,
              })),
            }}
          />
        </Col>
        <Col span={8}>
          <FieldBuilder
            name="type"
            label={`${t("type")}`}
            width="large"
            input={{
              type: "select",
              options: Object.values(NotificationType)
                .filter((value) => !isNaN(Number(value)))
                .map((type) => ({
                  label: t(getNotificationType(type as NotificationType)),
                  value: type as number,
                })),
              onChange: (value) => setSelectedType(value),
            }}
          />
        </Col>
        {selectedType && (
          <Col span={8}>
            <FieldBuilder
              name="itemId"
              label={t(selectedType === NotificationType.CHARITY 
                ? "charity" 
                : selectedType === NotificationType.CAMPAIGN 
                ? "campaign" 
                : "orphan")}
              width="large"
              input={{
                type: "select",
                loading: itemsLoading,
                allowSearch: true,
                options: items,
              }}
            />
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default GeneralNotificationFilter;